import type { ReactNode } from "react";
import type { Tab } from "./types";

export const icons: Record<string, string> = {
  menu: "M5 7h14M5 12h14M5 17h14",
  back: "M15 18l-6-6 6-6",
  close: "M6 6l12 12M18 6 6 18",
  shield: "M12 3 20 6v5.5c0 4.7-3 8.2-8 9.5-5-1.3-8-4.8-8-9.5V6l8-3",
  calendar: "M4 5h16v15H4V5Zm4-2v4m8-4v4M4 9h16",
  globe: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3 12h18M12 3c3 3 4 6 4 9s-1 6-4 9c-3-3-4-6-4-9s1-6 4-9",
  devices: "M3 5h13v10H3V5Zm4 14h5m-2-4v4m6-10h5v10h-5V9",
  clock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5l3 2",
  key: "M8 15a4 4 0 1 1 3.2-3.9L19 4m-3 3 2 2m-5 1 2 2",
  plus: "M12 5v14M5 12h14",
  refresh: "M4 12a8 8 0 1 0 3-6M4 4v6h6",
  arrow: "M5 12h14m-5-5 5 5-5 5",
  chevron: "M9 5l7 7-7 7",
  home: "M4 10.5 12 4l8 6.5V20h-5v-5H9v5H4v-9.5Z",
  user: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7.5 8c.7-4 3.2-6 7.5-6s6.8 2 7.5 6",
  more: "M5 12h.01M12 12h.01M19 12h.01",
  chat: "M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H10l-5 4v-4H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z",
  book: "M4 5.5C7 4.5 9 5 12 7v13c-3-2-5-2.5-8-1.5v-13Zm16 0c-3-1-5-.5-8 1.5v13c3-2 5-2.5 8-1.5v-13Z",
  gift: "M4 10h16v11H4V10Zm-1-4h18v4H3V6Zm9 0v15",
  users: "M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm8 1a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM3 20c.5-4 2.2-6 5-6s4.5 2 5 6m2-5c3 0 5 1.7 5.5 5",
  info: "M12 17h.01M12 7v6m0 9a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z",
  logout: "M10 5H5v14h5m4-4 4-3-4-3m4 3H9",
  telegram: "M4 11 20 4l-4 16-5-5-3 3v-5l8-6-10 5-2-1Z",
  lock: "M7 11V8a5 5 0 0 1 10 0v3m-11 0h12v9H6v-9Z",
  card: "M4 7h16v12H4V7Zm0 4h16",
};

export function Icon({ name, size = 22 }: { name: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={icons[name]}
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Header({
  onMenu,
  onBack,
  menuOpen = false,
}: {
  onMenu?: () => void;
  onBack?: () => void;
  menuOpen?: boolean;
}) {
  const isBack = Boolean(onBack);

  return (
    <header className="app-header">
      <span className="header-spacer" />
      <button
        className={menuOpen && !isBack ? "round-button pressable is-menu-open" : "round-button pressable"}
        type="button"
        onClick={onBack ?? onMenu}
        aria-label={isBack ? "Назад" : menuOpen ? "Закрыть меню" : "Открыть меню"}
        aria-expanded={!isBack ? menuOpen : undefined}
      >
        {isBack ? (
          <span className="header-icon">
            <Icon name="back" size={20} />
          </span>
        ) : (
          <span className="header-icon menu-glyph" aria-hidden="true">
            <span className="menu-bar" />
            <span className="menu-bar" />
            <span className="menu-bar" />
          </span>
        )}
      </button>
    </header>
  );
}

export function BottomNav({
  active,
  onTab,
}: {
  active: Tab;
  onTab: (tab: Tab) => void;
}) {
  const tabs: Array<[Tab, string, string]> = [
    ["home", "Главная", "home"],
    ["vpn", "VPN", "key"],
    ["profile", "Профиль", "user"],
    ["more", "Ещё", "more"],
  ];

  return (
    <nav className="bottom-nav">
      {tabs.map(([id, label, icon]) => (
        <button
          type="button"
          key={id}
          className={active === id ? "nav-item is-active" : "nav-item"}
          onClick={() => onTab(id)}
        >
          <Icon name={icon} size={20} />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}

export function Intro({ title, subtitle, compact = false }: { title: string; subtitle: string; compact?: boolean }) {
  const twoLineTitles: Record<string, [string, string]> = {
    "Войдите в AIDAHAR": ["Войдите в", "AIDAHAR"],
    "Реферальная программа": ["Реферальная", "программа"],
    "Добавить устройства": ["Добавить", "устройства"],
    "Оплата устройств": ["Оплата", "устройств"],
    "VPN не подключён": ["VPN не", "подключён"],
    "Сохранённая ссылка": ["Сохранённая", "ссылка"],
    "VPN снова активен": ["VPN снова", "активен"],
  };
  const lines = twoLineTitles[title];

  return (
    <section className={compact ? "intro is-compact" : "intro"}>
      <h1 className={lines ? "is-two-line-title" : undefined}>
        {lines ? <>{lines[0]}<br />{lines[1]}</> : title}
      </h1>
      <p>{subtitle}</p>
    </section>
  );
}

export function PrimaryButton({
  children,
  icon = "shield",
  onClick,
  disabled = false,
}: {
  children: ReactNode;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button type="button" className="primary-button pressable" onClick={onClick} disabled={disabled}>
      <Icon name={icon} />
      <span>{children}</span>
      <Icon name="arrow" />
    </button>
  );
}

export function SecondaryButton({
  children,
  icon,
  onClick,
  danger = false,
}: {
  children: ReactNode;
  icon?: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      className={danger ? "secondary-button pressable is-danger" : "secondary-button pressable"}
      onClick={onClick}
    >
      {icon && <Icon name={icon} size={20} />}
      <span>{children}</span>
    </button>
  );
}

export function InfoRow({
  icon,
  label,
  children,
  last = false,
}: {
  icon: string;
  label: string;
  children: ReactNode;
  last?: boolean;
}) {
  return (
    <div className={last ? "info-row is-last" : "info-row"}>
      <div className="info-label">
        <Icon name={icon} />
        <span>{label}</span>
      </div>
      <div className="info-value">{children}</div>
    </div>
  );
}

export function ActionRow({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="action-row pressable" onClick={onClick}>
      <span className="quick-icon"><Icon name={icon} size={20} /></span>
      <span className="action-copy">
        <strong>{title}</strong>
        {subtitle && <small>{subtitle}</small>}
      </span>
      <span className="grow" />
      <Icon name="chevron" size={18} />
    </button>
  );
}

export function MenuRow({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className="menu-row pressable" onClick={onClick}>
      <Icon name={icon} size={22} />
      <span className="menu-copy">
        <strong>{title}</strong>
        {subtitle && <small>{subtitle}</small>}
      </span>
      <span className="grow" />
      <Icon name="chevron" size={18} />
    </button>
  );
}

export function Notice({
  icon,
  title,
  text,
  tone = "default",
}: {
  icon: string;
  title: string;
  text: string;
  tone?: "default" | "success" | "warning";
}) {
  return (
    <div className={"notice " + "is-" + tone}>
      <Icon name={icon} size={22} />
      <div>
        <strong>{title}</strong>
        <small>{text}</small>
      </div>
    </div>
  );
}

export function EmptyCard({
  icon,
  title,
  text,
  tone = "blue",
}: {
  icon: string;
  title: string;
  text: ReactNode;
  tone?: "blue" | "warning";
}) {
  return (
    <div className="empty-card">
      <span className={tone === "warning" ? "empty-icon is-warning" : "empty-icon"}>
        <Icon name={icon} size={28} />
      </span>
      <strong>{title}</strong>
      <p>{text}</p>
    </div>
  );
}
